<template>
  <div ref="selectBoxRef" class="selectBox" :class="{ on: isOpen }">
    <button
      type="button"
      class="btnToggle"
      :class="{ selected: !!modelValue }"
      @click.stop="toggleDropdown"
    >
      {{ selectedLabel }}<span></span>
    </button>
    <div v-if="isOpen" class="dropMenu">
      <div class="searchBox form">
        <label for="country-select-search">
          <input
            id="country-select-search"
            ref="searchInputRef"
            v-model="search"
            type="search"
            class="search-input"
            :placeholder="t('register-country-region')"
            @keydown.enter.stop
            @keydown.down.prevent="onArrowDown"
            @keydown.up.prevent="onArrowUp"
            @keyup.enter.prevent.stop="onEnterCountry"
          />
        </label>
        <div class="btnArea flex item-center">
          <button
            v-if="search"
            type="button"
            class="btnClear"
            aria-label="Clear input"
            @click.stop="search = ''"
          ></button>
          <button type="button" class="btnSearch" aria-label="Search">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M13.9453 13.8848L19 18.9395" stroke="#838B9D" stroke-width="1.5" stroke-linecap="round"></path>
              <circle cx="10" cy="10" r="5.25" stroke="#838B9D" stroke-width="1.5"></circle>
            </svg>
          </button>
        </div>
      </div>
      <ul ref="listRef" class="scrollY">
        <li
          v-for="(country, index) in filtered"
          :key="country.nationCode"
          class="dropItem"
          :class="{ active: index === activeIndex }"
        >
          <button type="button" class="dropOption" @click.stop="selectCountry(country)">
            {{ countryLabel(country) }}
          </button>
        </li>
      </ul>
      <div v-if="filtered.length === 0" class="listNone">{{ t('common-none-search-data') }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'
import type { CountryModel } from '~~/package/mapper/user/model/CountryModel'
import { useUserService } from '~/composables/service/useUserService'

const props = defineProps<{
  modelValue: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const { t, locale } = useI18n()
const userService = useUserService()

const countryList = ref<CountryModel[]>([])
const isOpen = ref(false)
const search = ref('')
const activeIndex = ref(-1)

const selectBoxRef = ref<HTMLElement | null>(null)
const searchInputRef = ref<HTMLInputElement | null>(null)
const listRef = ref<HTMLElement | null>(null)

const countryLabel = (country: CountryModel): string => {
  const code = ` (${country.nationCode})`
  switch (locale.value) {
    case 'ko': return country.koreanName + code
    case 'ja': return country.japaneseName + code
    default: return country.englishName + code
  }
}

const filtered = computed<CountryModel[]>(() => {
  const keyword = search.value.toLowerCase()
  return keyword
    ? countryList.value.filter(c => countryLabel(c).toLowerCase().includes(keyword))
    : countryList.value
})

const selectedLabel = computed(() => {
  const found = countryList.value.find(c => c.nationCode === props.modelValue)
  return found ? countryLabel(found) : t('register-country-select')
})

const scrollTo = (align: 'auto' | 'top', index: number): void => {
  const container = listRef.value
  if (!container) return

  const buttons = container.querySelectorAll<HTMLButtonElement>('button.dropOption')
  if (!buttons.length || index < 0 || index >= buttons.length) return

  const item = buttons[index]
  const firstItemTop = buttons[0].offsetTop
  const itemTop = item.offsetTop
  const itemBottom = itemTop + item.offsetHeight
  const viewTop = container.scrollTop
  const viewBottom = viewTop + container.clientHeight

  if (align === 'top') {
    container.scrollTop = itemTop - firstItemTop
    return
  }
  if (itemBottom > viewBottom) {
    container.scrollTop = itemBottom - container.clientHeight
  } else if (itemTop < viewTop) {
    container.scrollTop = itemTop - firstItemTop
  }
}

const syncActiveIndex = (align: 'auto' | 'top' = 'auto'): void => {
  const idx = filtered.value.findIndex(c => c.nationCode === props.modelValue)
  activeIndex.value = idx >= 0 ? idx : (filtered.value.length > 0 ? 0 : -1)
  if (activeIndex.value >= 0) scrollTo(align, activeIndex.value)
}

const toggleDropdown = (): void => {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    search.value = ''
    nextTick(() => {
      searchInputRef.value?.focus()
      syncActiveIndex('top')
    })
  }
}

const selectCountry = (country: CountryModel): void => {
  emit('update:modelValue', country.nationCode)
  isOpen.value = false
  search.value = ''
}

const onArrowDown = (): void => {
  if (!filtered.value.length) return
  const next = activeIndex.value === -1 ? 0 : Math.min(activeIndex.value + 1, filtered.value.length - 1)
  activeIndex.value = next
  emit('update:modelValue', filtered.value[next].nationCode)
  scrollTo('auto', next)
}

const onArrowUp = (): void => {
  if (!filtered.value.length) return
  const prev = activeIndex.value === -1 ? 0 : Math.max(activeIndex.value - 1, 0)
  activeIndex.value = prev
  emit('update:modelValue', filtered.value[prev].nationCode)
  scrollTo('auto', prev)
}

const onEnterCountry = (): void => {
  if (!filtered.value.length) return
  if (activeIndex.value === -1) emit('update:modelValue', filtered.value[0].nationCode)
  isOpen.value = false
}

onClickOutside(selectBoxRef, () => {
  isOpen.value = false
})

watch(search, () => {
  if (!isOpen.value) return
  nextTick(() => syncActiveIndex('top'))
})

onMounted(() => {
  userService.getCountryList().then(list => {
    countryList.value = list
  })
})
</script>
